import { WEEK_TIME_COLORS } from '@utils/weekTimeBudget';

/** Marks the week donut in the top bar, where a session's time lands when it is marked used and the streak is hidden. */
export const WEEK_DONUT_ATTRIBUTE = 'data-week-donut';
/** The top bar timer pill's attribute, for the start animation to land on (timer spec step 3). */
export const TIMER_PILL_ATTRIBUTE = 'data-timer-pill';

/** Marks the study streak's flame in the top bar: while the streak shows, a session's time is thrown into it instead. */
export const STUDY_STREAK_FLAME_ATTRIBUTE = 'data-study-streak-flame';

const DOT_SIZE = 12;
// The dots trailing the first one: their size, how far behind it they set off, and how strongly they show.
const TRAIL = [
  { size: 9, delay: 45, opacity: 0.5 },
  { size: 7, delay: 90, opacity: 0.35 },
  { size: 5, delay: 135, opacity: 0.2 }
];
// Points along the curve; the browser draws straight between them, which at this many reads as the curve.
const PATH_STEPS = 32;

const easeInOut = (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

export const prefersLessMotion = () => !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// When the last dot thrown at the streak's flame lands, so the day's bar it boosted can stretch then rather than
// whenever the save happens to come back.
let streakLandsAt = 0;

/** How long until the dot last thrown at the streak's flame lands; 0 once it has. */
export const msUntilStreakLanding = () => Math.max(0, streakLandsAt - Date.now());

// When the last session's time thrown at the flame or the week donut lands: a session popup turning into an estimate
// question waits for it, so its cover doesn't rise while the dot is still flying (Petr, 2026-10-31: wait for the dot).
let timeLandsAt = 0;

/** How long until the time last thrown at the flame or the week donut lands; 0 once it has. */
export const msUntilTimeLands = () => Math.max(0, timeLandsAt - Date.now());

// When what the last clicks set moving has stopped: an estimate question that opens as an overlay waits for it, rather
// than landing on a dot still flying or a popup still shrinking (Petr, 2026-10-31: one thing moving at a time).
let stillAt = 0;

/** Something has started moving for the next `ms`. */
export const noteMovement = (ms: number) => {
  stillAt = Math.max(stillAt, Date.now() + ms);
};

/** How long until everything noted as moving has stopped; 0 once it has. */
export const msUntilStill = () => Math.max(0, stillAt - Date.now());

// Sessions just marked used, so the calendar's used badge on their block bounces in once when it first shows, and not
// on every later render. Forgotten after a few seconds, in case the save never comes back.
const justMarkedUsed = new Map<string, number>();

/** Whether the block was just marked used here; true once, for the render that first shows its used badge. */
export const takeJustMarkedUsed = (plannedTaskId: string | number) => {
  const markedAt = justMarkedUsed.get(String(plannedTaskId));
  justMarkedUsed.delete(String(plannedTaskId));
  return markedAt !== undefined && Date.now() - markedAt < 5000;
};

/**
 * Runs once the next frame is on screen. Saving a session redraws the calendar and freezes the page for a moment
 * (about 150 ms on the click, half a second or more when the save comes back, measured locally); started after a
 * frame, the animations below are already running when it does, and the browser keeps them going through it.
 */
export const afterNextPaint = (run: () => void) => {
  if (typeof window.requestAnimationFrame !== 'function') {
    run();
    return;
  }
  window.requestAnimationFrame(() => window.setTimeout(run, 0));
};

const fixedDot = (x: number, y: number, size: number) => {
  const dot = document.createElement('div');
  Object.assign(dot.style, {
    position: 'fixed',
    left: `${x - size / 2}px`,
    top: `${y - size / 2}px`,
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: '50%',
    background: WEEK_TIME_COLORS.usedStudy,
    pointerEvents: 'none',
    zIndex: '10000',
    willChange: 'transform, opacity'
  });
  document.body.appendChild(dot);
  return dot;
};

/**
 * Sends a dot of aqua from the button up into `target`, with a few smaller dots trailing it, landing `aimY` of the way
 * down it. Returns where and after how long it lands, for the landing to be timed to it; nothing when either is off
 * screen.
 *
 * All of it, landing included, is worked out up front and handed to the browser as one animation per element, which
 * it runs on its own: a dot moved frame by frame from here stuttered while the calendar redrew (Petr: "a little
 * jerky"), and a landing started when the flight's end was reported came late for the same reason.
 */
const flyTo = (from: Element | DOMRect, target: Element, aimY = 0.5) => {
  const start = from instanceof Element ? from.getBoundingClientRect() : from;
  const end = target.getBoundingClientRect();
  if (!end.width || !start.width) return null;

  const x0 = start.left + start.width / 2;
  const y0 = start.top + start.height / 2;
  const x1 = end.left + end.width / 2;
  const y1 = end.top + end.height * aimY;
  // Rises first, then curves across into the target.
  const cx = x0 + (x1 - x0) * 0.15;
  const cy = y1 + 10;
  const duration = Math.min(900, Math.max(550, 450 + Math.hypot(x1 - x0, y1 - y0) * 0.4));

  const flight = (strength: number, fadeOut: boolean): Keyframe[] =>
    Array.from({ length: PATH_STEPS + 1 }, (_, index) => {
      const t = index / PATH_STEPS;
      const p = easeInOut(t);
      const x = (1 - p) * (1 - p) * x0 + 2 * (1 - p) * p * cx + p * p * x1;
      const y = (1 - p) * (1 - p) * y0 + 2 * (1 - p) * p * cy + p * p * y1;
      // Swells a little as it lifts off, then shrinks into the target, where it melts away.
      const scale = t < 0.4 ? 1 + t * 0.5 : 1.2 - (t - 0.4) * 1.2;
      const fade = fadeOut ? 1 - t * 0.6 : t > 0.85 ? (1 - t) / 0.15 : 1;
      return { offset: t, transform: `translate(${x - x0}px, ${y - y0}px) scale(${scale})`, opacity: strength * fade };
    });

  // Every dot is hidden outside its own flight, so one left a moment before it is removed doesn't show anywhere.
  const launch = (size: number, keyframes: Keyframe[], delay: number) => {
    const dot = fixedDot(x0, y0, size);
    dot.style.opacity = '0';
    dot.animate(keyframes, { duration, delay, easing: 'linear' });
    window.setTimeout(() => dot.remove(), duration + delay + 100);
  };
  TRAIL.forEach(({ size, delay, opacity }) => launch(size, flight(opacity, true), delay));
  launch(DOT_SIZE, flight(1, false), 0);

  return { x: x1, y: y1, top: end.top, landsAfter: duration - 40 };
};

/**
 * The week donut takes the time in, where it now counts as "Used study time" (Petr: the button should be more fun to
 * click, and this also shows where the time goes): the donut bumps and an aqua ring spreads from it.
 */
const flyToWeekDonut = (from: Element, donut: HTMLElement) => {
  const landing = flyTo(from, donut);
  if (!landing) return;
  timeLandsAt = Date.now() + landing.landsAfter;
  donut.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.35)' }, { transform: 'scale(1)' }], {
    duration: 380,
    delay: landing.landsAfter,
    easing: 'ease-out'
  });
  const ring = fixedDot(landing.x, landing.y, 24);
  Object.assign(ring.style, { background: 'none', border: `2px solid ${WEEK_TIME_COLORS.usedStudy}`, opacity: '0' });
  ring.animate(
    [
      { transform: 'scale(0.6)', opacity: 1 },
      { transform: 'scale(1.9)', opacity: 0 }
    ],
    { duration: 480, delay: landing.landsAfter, easing: 'ease-out' }
  );
  window.setTimeout(() => ring.remove(), landing.landsAfter + 580);
  noteMovement(landing.landsAfter + 480);
};

// The embers a fed flame gives off: how far right of its middle they start, how far they drift and rise, their size,
// and how long after landing they set off.
const EMBERS = [
  { x: -3, drift: -7, rise: 20, size: 4, delay: 60 },
  { x: 2, drift: 5, rise: 26, size: 5, delay: 0 },
  { x: 0, drift: -2, rise: 30, size: 3, delay: 140 },
  { x: 4, drift: 8, rise: 17, size: 3, delay: 100 },
  { x: -1, drift: 2, rise: 23, size: 4, delay: 200 }
];

/**
 * The streak's flame takes the time in (Petr: throw it into the fire, as if it helped the streak grow): it flares up
 * from its base with a little flicker, and a few embers rise off it and fade.
 */
const flyToStreakFlame = (from: Element, flame: HTMLElement | SVGElement) => {
  const landing = flyTo(from, flame, 0.65);
  if (!landing) return;
  streakLandsAt = Date.now() + landing.landsAfter;
  timeLandsAt = streakLandsAt;
  // Until its flare is over; the embers are small enough to fade under whatever comes next.
  noteMovement(landing.landsAfter + 700);
  // No glow: a blurred shadow round the flame looked off-centre (Petr, 2026-10-09). The flare and embers say it.
  flame.style.transformOrigin = '50% 90%';
  flame.animate(
    [
      { transform: 'scale(1) rotate(0deg)' },
      { offset: 0.3, transform: 'scale(1.5) rotate(-7deg)' },
      { offset: 0.55, transform: 'scale(0.92) rotate(5deg)' },
      { offset: 0.8, transform: 'scale(1.08) rotate(-2deg)' },
      { transform: 'scale(1) rotate(0deg)' }
    ],
    { duration: 700, delay: landing.landsAfter, easing: 'ease-out' }
  );
  EMBERS.forEach(({ x, drift, rise, size, delay }) => {
    const ember = fixedDot(landing.x + x, landing.top + 4, size);
    ember.style.opacity = '0';
    const duration = 700 + rise * 5;
    ember.animate(
      [
        { transform: 'translate(0, 0) scale(1)', opacity: 1 },
        { transform: `translate(${drift}px, -${rise}px) scale(0.4)`, opacity: 0 }
      ],
      { duration, delay: landing.landsAfter + delay, easing: 'ease-out' }
    );
    window.setTimeout(() => ember.remove(), landing.landsAfter + delay + duration + 100);
  });
};

/** The used time goes into the streak's flame while the streak shows, otherwise into the week donut. */
const flyToWeekTime = (from: Element) => {
  const flame = document.querySelector<HTMLElement | SVGElement>(`[${STUDY_STREAK_FLAME_ATTRIBUTE}]`);
  if (flame) {
    flyToStreakFlame(from, flame);
    return;
  }
  const donut = document.querySelector<HTMLElement>(`[${WEEK_DONUT_ATTRIBUTE}]`);
  if (donut) flyToWeekDonut(from, donut);
};

/**
 * What marking a session used looks like, from the calendar's "✓ Used?" or the session popup's ✓: the button gives a
 * little bounce, its time flies into the study streak's flame (or the week donut, with the streak hidden), and the
 * block's used badge on the calendar bounces in once.
 * Nothing moves for a student who asks for less motion. Save the session with `afterNextPaint`, so this is on screen
 * before the redraw.
 */
export const celebrateMarkedUsed = (button: Element, plannedTaskId: string | number) => {
  justMarkedUsed.set(String(plannedTaskId), Date.now());
  if (prefersLessMotion() || typeof button.animate !== 'function') return;
  button.animate(
    [{ transform: 'scale(1)' }, { transform: 'scale(0.82)' }, { transform: 'scale(1.15)' }, { transform: 'scale(1)' }],
    { duration: 320, easing: 'ease-out' }
  );
  flyToWeekTime(button);
};

// The play button a timer was just started from (timer spec step 3, version A): the top bar's pill only shows once the
// start is saved, so it takes the flight from here when it appears.
// Where it was is kept too: a calendar block's ▶ is gone by the time the pill shows (the block turns into a running
// one and moves), so the dot flies from the spot it was clicked (Petr, 2026-10-15: no flight from the calendar).
let pendingTimerStart: { from: Element; rect: DOMRect; at: number } | null = null;

/** Remembers the play button a timer is being started from, for the pill to fly the start into. */
export const noteTimerStart = (from: Element) => {
  pendingTimerStart = { from, rect: from.getBoundingClientRect(), at: Date.now() };
};

/**
 * The block a timer was started from has slid to now (floatToNow.ts): the start flies from where it is now, not from
 * the empty spot it left (Petr, 2026-10-18). A start from the session popup still flies from the popup's ▶.
 */
export const moveTimerStart = (to: Element) => {
  if (!pendingTimerStart) return;
  const { from, at } = pendingTimerStart;
  if (from.isConnected && !from.closest('.fc-timegrid-event')) return;
  pendingTimerStart = { from: to, rect: to.getBoundingClientRect(), at };
};

/** The play button a timer was just started from, once (or where it was, if it has left the page); not after a few seconds. */
export const takeTimerStart = (): Element | DOMRect | null => {
  const pending = pendingTimerStart;
  pendingTimerStart = null;
  if (!pending || Date.now() - pending.at >= 3000) return null;
  return pending.from.isConnected ? pending.from : pending.rect;
};

// "Go to active timer" (▶ pressed while another session's timer runs): the top bar's pill opens the running session's
// popup under it, rather than the big task window (Petr, 2026-10-16).
const showRunningTimerListeners = new Set<() => void>();

/** Opens the running timer's popup under the top bar's pill. */
export const showRunningTimer = () => showRunningTimerListeners.forEach(listener => listener());

/** The pill's side of showRunningTimer: returns the unsubscribe. */
export const onShowRunningTimer = (listener: () => void) => {
  showRunningTimerListeners.add(listener);
  return () => {
    showRunningTimerListeners.delete(listener);
  };
};

/** A small nudge, so the eye finds the pill as its popup opens. */
export const nudgePill = (pill: HTMLElement) => {
  if (prefersLessMotion() || typeof pill.animate !== 'function') return;
  pill.animate(
    [{ transform: 'scale(1)' }, { transform: 'scale(1.08)' }, { transform: 'scale(0.97)' }, { transform: 'scale(1)' }],
    { duration: 420, easing: 'ease-out' }
  );
};

// A click on the timer's desktop notice (Petr, 2026-11-01): Shovel comes forward and the pill pulses, so the eye lands on
// what it says. Slow and soft, and a few beats long, as the window may still be coming forward during the first.
const PULSE_BEAT_MS = 1100;
const PULSE_BEATS = 3;
let pillPulse: Animation | undefined;

/**
 * Pulses the top bar's pill in its own colour: its fill deepens a little while a soft ring spreads from it and fades. For
 * a student who asks for less motion the ring stays where it is and only fades in and out, as this is all a click on the
 * notice shows.
 */
export const pulseTimerPill = () => {
  const pill = document.querySelector<HTMLElement>(`[${TIMER_PILL_ATTRIBUTE}]`);
  if (!pill || typeof pill.animate !== 'function') return;
  const { borderTopColor: own, backgroundColor: fill } = window.getComputedStyle(pill);
  const deeper = `color-mix(in srgb, ${own} 30%, ${fill})`;
  const beat: Keyframe[] = prefersLessMotion()
    ? [
        { offset: 0, boxShadow: '0 0 0 3px transparent', backgroundColor: fill },
        { offset: 0.35, boxShadow: `0 0 0 3px ${own}`, backgroundColor: deeper },
        { offset: 1, boxShadow: '0 0 0 3px transparent', backgroundColor: fill }
      ]
    : [
        { offset: 0, boxShadow: `0 0 0 0 ${own}`, backgroundColor: fill },
        { offset: 0.35, backgroundColor: deeper },
        { offset: 1, boxShadow: '0 0 0 10px transparent', backgroundColor: fill }
      ];
  pillPulse?.cancel();
  pillPulse = pill.animate(beat, { duration: PULSE_BEAT_MS, iterations: PULSE_BEATS, easing: 'ease-out' });
};

// The top bar's pill opening and closing (Petr, 2026-10-17: "very jumpy"): it closes back into its pause button; the room it takes in the bar opens and folds smoothly, so the streak
// beside it glides rather than jumps. Light: a clip and a width, no more.
const PILL_UNROLL_MS = 260;
const PILL_ROOM_MS = 300;
const PILL_EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';
const PILL_SHOWN = 'inset(0 0 0 0 round 999px)';
const PILL_HIDDEN = 'inset(0 100% 0 0 round 999px)';

/**
 * Closes the top bar's pill: it wipes back into its pause button, right to left, then its room folds and the streak
 * glides back. Returns how long that takes, to remove the pill after it; 0 with nothing to animate.
 */
export const closeTimerPill = (): number => {
  const pill = document.querySelector<HTMLElement>(`[${TIMER_PILL_ATTRIBUTE}]`);
  const room = pill?.parentElement;
  if (!pill || !room || prefersLessMotion() || typeof pill.animate !== 'function') return 0;
  const width = room.getBoundingClientRect().width;
  pill.animate([{ clipPath: PILL_SHOWN }, { clipPath: PILL_HIDDEN }], {
    duration: PILL_UNROLL_MS,
    easing: PILL_EASE,
    fill: 'forwards'
  });
  room.style.overflow = 'hidden';
  room.animate([{ width: `${width}px` }, { width: '0px' }], {
    duration: PILL_ROOM_MS,
    delay: PILL_UNROLL_MS,
    easing: PILL_EASE,
    fill: 'forwards'
  });
  return PILL_UNROLL_MS + PILL_ROOM_MS;
};

/**
 * Starting a timer (Petr, 2026-10-17, board 3 version B, refined): the timer flies out of the ▶ that was pressed, in
 * the session's popup or on its calendar block, as just its ring and time, sized like that button. It glides up to
 * where the pill's ring and time will sit while the pill's room opens beside the streak (so the streak glides aside),
 * then opens out into the whole pill: pause on the left, the session's name and ✓ on the right. A copy of the pill,
 * clipped to its ring and time, does the flying. No dot: a flying dot means time logged.
 */
const LIFT_FLY_MS = 520;
const LIFT_OPEN_MS = 300;

export const liftTimeIntoPill = (from: Element | DOMRect, pill: HTMLElement) => {
  if (prefersLessMotion() || typeof pill.animate !== 'function') return;
  const room = pill.parentElement;
  // Where everything ends up: measured before the room folds for the opening.
  const end = pill.getBoundingClientRect();
  const start = from instanceof Element ? from.getBoundingClientRect() : from;
  const parts = pill.querySelectorAll<HTMLElement>('button');
  const middle = parts[1];
  const ring = middle?.querySelector('svg');
  const clock = middle?.querySelectorAll('span');
  if (!end.width || !start.width || !ring || !clock?.length) return;

  // The ring and the time ("28:13 left") as one rounded chip inside the pill.
  const timeEnd = Array.from(clock)
    .slice(0, 2)
    .reduce((right, span) => Math.max(right, span.getBoundingClientRect().right), 0);
  const left = Math.max(0, ring.getBoundingClientRect().left - end.left - 6);
  const right = Math.max(0, end.right - timeEnd - 8);
  const chipClip = `inset(0 ${right}px 0 ${left}px round 999px)`;
  const fullClip = 'inset(0 0 0 0 round 999px)';
  const chipCenterX = end.left + left + (end.width - left - right) / 2;
  const centerY = end.top + end.height / 2;

  const chip = pill.cloneNode(true) as HTMLElement;
  chip.removeAttribute(TIMER_PILL_ATTRIBUTE);
  chip.setAttribute('aria-hidden', 'true');
  Object.assign(chip.style, {
    position: 'fixed',
    left: `${end.left}px`,
    top: `${end.top}px`,
    width: `${end.width}px`,
    height: `${end.height}px`,
    margin: '0',
    zIndex: '10000',
    pointerEvents: 'none',
    boxSizing: 'border-box',
    clipPath: chipClip,
    transformOrigin: `${chipCenterX - end.left}px 50%`
  });
  document.body.appendChild(chip);

  const scale = Math.max(0.6, Math.min(1.25, start.height / end.height));
  const dx = start.left + start.width / 2 - chipCenterX;
  // It comes out just above the ▶, not over it: fading in on top of it, its ring and time sat half over the popup's
  // own ring and clock and showed double for a moment (Petr, 2026-10-18: "flashes once when it first appears"). Quick to
  // show, so it is barely seen over the mode switch above the ▶.
  const dy = start.top - (end.height * scale) / 2 - centerY;
  chip.animate(
    [
      { transform: `translate(${dx}px, ${dy + 6}px) scale(${scale})`, opacity: 0 },
      { offset: 0.08, transform: `translate(${dx}px, ${dy - 4}px) scale(${scale * 1.04})`, opacity: 1 },
      { transform: 'translate(0, 0) scale(1)', opacity: 1 }
    ],
    { duration: LIFT_FLY_MS, easing: 'cubic-bezier(0.5, 0, 0.25, 1)', fill: 'forwards' }
  );
  window.setTimeout(() => chip.remove(), LIFT_FLY_MS + 40);

  // The real pill takes over where the chip lands, then opens out from it to its whole width.
  const landed = LIFT_FLY_MS / (LIFT_FLY_MS + LIFT_OPEN_MS);
  pill.animate(
    [
      { offset: 0, opacity: 0, clipPath: chipClip },
      { offset: landed, opacity: 0, clipPath: chipClip },
      { offset: landed + 0.001, opacity: 1, clipPath: chipClip, easing: 'cubic-bezier(0.2, 0, 0, 1)' },
      { offset: 1, opacity: 1, clipPath: fullClip }
    ],
    { duration: LIFT_FLY_MS + LIFT_OPEN_MS }
  );

  if (room) {
    room.style.overflow = 'hidden';
    room
      .animate([{ width: '0px' }, { width: `${room.getBoundingClientRect().width}px` }], {
        duration: LIFT_FLY_MS,
        easing: PILL_EASE,
        fill: 'backwards'
      })
      .addEventListener('finish', () => {
        room.style.overflow = '';
      });
  }
};
